/* eslint-disable react/no-unescaped-entities */
import React from 'react';

const sectionHeadingClass = 'text-2xl font-semibold sm:text-xl';
const subheadingClass = 'text-xl font-medium mt-4 sm:text-lg';
const listClass = 'list-disc ml-6 sm:ml-4';
const tableClass =
  'w-full min-w-[640px] border-collapse border border-black-400 mt-4 text-sm sm:text-xs';
const cellClass = 'border border-black-400 p-2 text-left align-top';

const CareerPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 text-black sm:p-4 sm:max-w-full md:p-8 lg:p-10 overflow-x-auto">
      <h1 className="text-3xl font-bold mb-6 text-center sm:text-2xl">PW Career Services Policy</h1>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 1: INTRODUCTION AND GENERAL PROVISIONS</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>1.1 Purpose of the Policy</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Inform learners about the terms, processes and procedures of Career Services provided by
            PW Skills.
          </li>
          <li>
            Define expected learner conduct throughout Career Services and recruitment processes.
          </li>
          <li>Define provisions and procedures for accepting job and internship offers.</li>
          <li>
            Establish a standardized Career Readiness framework covering technical competency,
            employability skills, interview preparedness and overall career readiness.
          </li>
          <li>
            Use periodic assessments and mock interviews to generate a transparent Career Readiness
            Score that governs access levels to Career Services opportunities.
          </li>
          <li>
            Encourage continuous improvement through recurring assessments and a merit-based
            allocation of placement and internship opportunities.
          </li>
        </ul>

        <h3 className={subheadingClass}>
          <strong>1.2 Applicability</strong>
        </h3>
        <ul className={listClass}>
          <li>
            This policy applies to learners enrolled in PW Skills programs where Career Services
            and/or Career Readiness Assessments are conducted.
          </li>
          <li>
            Participation in Career Readiness Assessments is mandatory for learners seeking Career
            Services support.
          </li>
          <li>
            Career Services provisions remain applicable until three months after completion of the
            respective batch, subject to eligibility and active Career Services status.
          </li>
          <li>
            Only learners meeting the applicable course completion, eligibility and Career Readiness
            requirements may access Career Services opportunities.
          </li>
        </ul>

        <h3 className={subheadingClass}>
          <strong>1.3 Disclaimer</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Career Services is a facility offered by PW Skills and does not guarantee job offers,
            internships, interviews or employment.
          </li>
          <li>
            A Career Readiness Score is an assessment/readiness indicator and does not constitute a
            placement guarantee.
          </li>
          <li>PW Skills reserves the right to modify this policy as deemed appropriate.</li>
          <li>
            Learners are not entitled to any fee refund or reimbursement on account of Career
            Services eligibility, withdrawal or disqualification.
          </li>
          <li>
            English is mandatory for Career Services communication, hiring drives, interviews and
            career support.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 2: CAREER READINESS PROGRAM (CRP)</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>Objective</strong>
        </h3>
        <ul className={listClass}>
          <li>
            To assess and strengthen learners' technical, communication, and interview readiness
            through a structured, multi-stage assessment framework.
          </li>
          <li>
            To provide a measurable Career Readiness Score that determines placement readiness,
            Career Services access, and certification eligibility.
          </li>
        </ul>

        <h3 className={subheadingClass}>
          <strong>2.1 Definitions</strong>
        </h3>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr className="bg-black-200">
                <th className={cellClass}>Term</th>
                <th className={cellClass}>Definition</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={cellClass}>Career Readiness Score</td>
                <td className={cellClass}>
                  Consolidated score out of 100 representing overall placement readiness.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>Technical Test</td>
                <td className={cellClass}>
                  Assessment of domain knowledge, technical understanding and problem solving.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>AI Mock Interview</td>
                <td className={cellClass}>
                  AI simulated interviews assessing communication, confidence and interview
                  readiness.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>Human Mock Interview</td>
                <td className={cellClass}>
                  Live mock interview conducted by an assigned mentor, trainer or industry
                  professional.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>Readiness Level</td>
                <td className={cellClass}>
                  Career Services placement category assigned based on the latest Career Readiness
                  Score.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className={subheadingClass}>
          <strong>2.2 Assessment Framework</strong>
        </h3>
        <p>
          The CRP consists of four stages. Each stage carries 100 marks and contributes 25% to the
          Final Career Readiness Score.
        </p>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr className="bg-black-200">
                <th className={cellClass}>Stage</th>
                <th className={cellClass}>Components</th>
                <th className={cellClass}>Marks</th>
                <th className={cellClass}>Weightage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={cellClass}>Stage 1</td>
                <td className={cellClass}>Technical Test + AI Mock Interview</td>
                <td className={cellClass}>100</td>
                <td className={cellClass}>25%</td>
              </tr>
              <tr>
                <td className={cellClass}>Stage 2</td>
                <td className={cellClass}>
                  Technical Test + AI Mock Interview + Human Mock Interview (Pro learners)
                </td>
                <td className={cellClass}>100</td>
                <td className={cellClass}>25%</td>
              </tr>
              <tr>
                <td className={cellClass}>Stage 3</td>
                <td className={cellClass}>Technical Test + AI Mock Interview</td>
                <td className={cellClass}>100</td>
                <td className={cellClass}>25%</td>
              </tr>
              <tr>
                <td className={cellClass}>Stage 4</td>
                <td className={cellClass}>
                  Technical Test + AI Mock Interview + Human Mock Interview (Pro learners)
                </td>
                <td className={cellClass}>100</td>
                <td className={cellClass}>25%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className={subheadingClass}>
          <strong>2.3 Final Career Readiness Score</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Final Career Readiness Score = (Stage 1 x 25%) + (Stage 2 x 25%) + (Stage 3 x 25%) +
            (Stage 4 x 25%).
          </li>
          <li>Each stage contributes equally to the final score.</li>
          <li>
            The latest Career Readiness Score recorded in the system is the active score for Career
            Services eligibility and access level.
          </li>
        </ul>

        <h3 className={subheadingClass}>
          <strong>2.4 Certification</strong>
        </h3>
        <ul className={listClass}>
          <li>
            <strong>Direct Certification:</strong> Certificate awarded when attendance is 60% or
            higher in live classes and the Final Career Readiness Score is 40% or higher.
          </li>
          <li>
            <strong>Remedial Eligibility:</strong> Learners who do not qualify for direct
            certification but maintain 50% or higher attendance in live classes may take a remedial
            assessment.
          </li>
          <li>
            <strong>Remedial Assessment:</strong> Conducted within 2 weeks of result declaration;
            components are Technical Test + AI Mock Interview; maximum marks 100.
          </li>
          <li>
            <strong>Remedial Result:</strong> Score of 40% or higher results in certification; below
            40% results in no certificate.
          </li>
          <li>
            No certification is awarded where attendance in live classes is below 50%, the remedial
            score is below 40%, or misconduct is detected during assessment.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 3: CAREER SERVICES ELIGIBILITY</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>3.1 Eligibility</strong>
        </h3>
        <p>
          To qualify for job opportunities facilitated by PW Skills, learners must meet the
          applicable criteria below.
        </p>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr className="bg-black-200">
                <th className={cellClass}>Criteria</th>
                <th className={cellClass}>Non-Tech Learners (DM &amp; DA)</th>
                <th className={cellClass}>Tech Learners (FSD &amp; DS)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={cellClass}>Education</td>
                <td className={cellClass}>Graduation and above</td>
                <td className={cellClass}>Graduation and above (B.Tech, B.E., BCA, MCA ONLY)</td>
              </tr>
              <tr>
                <td className={cellClass}>Year of Graduation/Post Graduation</td>
                <td className={cellClass}>
                  Learners who graduated within the last 5 years or are graduating in the current or
                  upcoming academic year are eligible. For example, in 2026, only 2021-2027
                  graduates are eligible.
                </td>
                <td className={cellClass}>
                  Learners who graduated within the last 5 years or are graduating in the current or
                  upcoming academic year are eligible. For example, in 2026, only 2021-2027
                  graduates are eligible.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>Minimum Academic Scores</td>
                <td className={cellClass}>
                  Drive-specific criteria are determined at the company's discretion; PW has no role
                  in this determination.
                </td>
                <td className={cellClass}>
                  Drive-specific criteria are determined at the company's discretion; PW has no role
                  in this determination.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>Career Services Platform</td>
                <td className={cellClass}>Registration and full participation mandatory</td>
                <td className={cellClass}>Registration and full participation mandatory</td>
              </tr>
              <tr>
                <td className={cellClass}>Policy Adherence</td>
                <td className={cellClass}>Mandatory</td>
                <td className={cellClass}>Mandatory</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className={subheadingClass}>
          <strong>3.2 Career Readiness Certification Requirement</strong>
        </h3>
        <ul className={listClass}>
          <li>
            For programs covered by the CRP, Career Readiness certification is mandatory for access
            to Career Services opportunities.
          </li>
          <li>
            The CRP does not replace the baseline eligibility criteria in Section 3.1; learners must
            satisfy both the applicable baseline requirements and the CRP requirements.
          </li>
          <li>
            The active Career Readiness Score determines the learner's Career Services access level,
            subject to drive-specific eligibility.
          </li>
        </ul>

        <h3 className={subheadingClass}>
          <strong>3.3 Career Readiness Assessment Attendance</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Learners must maintain a minimum 60% attendance in all live classes during the
            applicable assessment cycle to be eligible for that stage's Career Readiness Assessment.
          </li>
          <li>
            Learners below 60% attendance during the assessment cycle will not be permitted to
            appear for that assessment stage, with no exceptions under the CRP.
          </li>
        </ul>

        <h3 className={subheadingClass}>
          <strong>3.4 Additional Eligibility Considerations</strong>
        </h3>
        <p className="mt-2 mb-1">
          <strong>Career Gaps:</strong>
        </p>
        <ul className={listClass}>
          <li>
            Learners with a career gap of more than 2 years will not be eligible for placement
            opportunities.
          </li>
          <li>
            Learners with a gap of up to 2 years may be considered only if they provide valid proof
            of productive engagement such as certifications, training, freelancing or volunteering,
            subject to verification.
          </li>
        </ul>
        <p className="mt-2 mb-1">
          <strong>Profile Relevance and Domain Shift:</strong>
        </p>
        <ul className={listClass}>
          <li>
            Learners transitioning to a different domain will be assessed as freshers for the target
            role.
          </li>
          <li>
            Eligibility, assessments and opportunities will be aligned with entry-level criteria,
            regardless of prior experience in an unrelated field.
          </li>
          <li>Compensation (CTC) will follow fresher benchmarks.</li>
          <li>
            Current hiring trends largely follow an Intern to PPO model for early-career
            opportunities.
          </li>
        </ul>
        <p className="mt-2 mb-1">
          <strong>Graduation Year / Experience Relevance:</strong>
        </p>
        <ul className={listClass}>
          <li>
            Placement opportunities may be limited for learners with older graduation years,
            especially without relevant domain experience.
          </li>
          <li>Most entry-level roles prefer candidates who graduated within the last 2-3 years.</li>
          <li>
            Many opportunities may begin as internships, with stipends typically ranging from
            ₹10,000-₹25,000 per month, depending on company policies.
          </li>
        </ul>
        <p className="mt-2 mb-1">
          <strong>Work-from-Home (WFH) Expectations:</strong>
        </p>
        <ul className={listClass}>
          <li>WFH opportunities are subject to company requirements and role suitability.</li>
          <li>
            No assurance or commitment regarding WFH roles should be made unless explicitly
            confirmed by the hiring company.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 4: CAREER SERVICES REGISTRATION AND COMMUNICATION</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>4.1 Registration Process</strong>
        </h3>
        <ul className={listClass}>
          <li>A dedicated Career Services Platform (CSP) will be provided.</li>
          <li>Learners must complete timely registration to access Career Services.</li>
          <li>
            Learners must provide accurate information, including contact details, previous
            qualifications, LinkedIn profile link, study-gap details, relevant links and employment
            history.
          </li>
        </ul>
        <h3 className={subheadingClass}>
          <strong>4.2 Learner Contact Details &amp; Communication</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Learners should not change their contact details because these details may be shared
            with recruiters.
          </li>
          <li>
            PW Skills is not responsible for communication issues arising from unreachable phone
            numbers or email addresses.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 5: CAREER SERVICES ACCESS LEVELS</strong>
        </h2>
        <p>
          Certification is mandatory for Career Services access. Subject to baseline eligibility and
          company-specific drive criteria, access is assigned according to the active Career
          Readiness Score.
        </p>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr className="bg-black-200">
                <th className={cellClass}>Readiness Score</th>
                <th className={cellClass}>Level</th>
                <th className={cellClass}>Internship Eligibility</th>
                <th className={cellClass}>Full-Time Opportunities</th>
                <th className={cellClass}>Job Nature</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={cellClass}>40% to less than 50%</td>
                <td className={cellClass}>Level 1</td>
                <td className={cellClass}>Up to ₹10,000 stipend</td>
                <td className={cellClass}>₹2.5-3.5 LPA</td>
                <td className={cellClass}>Non-Core &amp; Core Roles</td>
              </tr>
              <tr>
                <td className={cellClass}>50% to less than 70%</td>
                <td className={cellClass}>Level 2</td>
                <td className={cellClass}>Up to ₹20,000 stipend</td>
                <td className={cellClass}>₹2.5-6 LPA</td>
                <td className={cellClass}>Core</td>
              </tr>
              <tr>
                <td className={cellClass}>70% and above</td>
                <td className={cellClass}>Level 3</td>
                <td className={cellClass}>All internship opportunities</td>
                <td className={cellClass}>All eligible opportunities</td>
                <td className={cellClass}>Core</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3 className={subheadingClass}>
          <strong>5.1 Interview Allocation</strong>
        </h3>
        <ul className={listClass}>
          <li>
            <strong>
              All readiness levels receive a maximum of five interview opportunities within the
              three-month Career Services period.
            </strong>
          </li>
          <li>
            Interview allocation remains subject to company-specific eligibility, learner
            participation requirements and the One Learner - One Offer Policy.
          </li>
        </ul>
        <h3 className={subheadingClass}>
          <strong>5.2 Readiness Score Improvement</strong>
        </h3>
        <ul className={listClass}>
          <li>
            PW Skills may periodically conduct Career Readiness Assessments to enable learners to
            improve their score.
          </li>
          <li>Frequency may be every 15 days, or as notified by PW Skills.</li>
          <li>
            Improvement assessments consist of a Technical Test + AI Mock Interview, with maximum
            marks of 100.
          </li>
          <li>
            The latest score is always the active score. Improved scores may unlock higher-level
            opportunities; a lower subsequent score may revise eligibility downward accordingly.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 6: RECRUITMENT PROCESS AND PARTICIPATION</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>6.1 Recruitment Process</strong>
        </h3>
        <ul className={listClass}>
          <li>CV/Portfolio Shortlisting</li>
          <li>Online/Written Tests</li>
          <li>Interviews (Offline / Virtual)</li>
          <li>Group Discussions (GDs)</li>
        </ul>
        <p className="mt-2 mb-1">
          <strong>Types of Recruitment Drives:</strong>
        </p>
        <ul className={listClass}>
          <li>Online Placement Drive</li>
          <li>Offline Placement Drive</li>
          <li>Internship/Training Drive</li>
        </ul>
        <p className="mt-2 mb-1">
          <strong>Important Notes:</strong>
        </p>
        <ul className={listClass}>
          <li>
            Career opportunities can be anywhere in India; a 'jobs-in-my-city' approach is not
            entertained.
          </li>
          <li>Learners must travel at their own expense for in-person hiring drives.</li>
          <li>Resumes must follow PW Skills-provided templates.</li>
        </ul>
        <h3 className={subheadingClass}>
          <strong>6.2 Participation in Hiring Drives</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Learners may participate when they meet the applicable drive eligibility criteria.
          </li>
          <li>Learners must complete all interview rounds if shortlisted.</li>
          <li>No rescheduling of interviews is allowed.</li>
          <li>
            If a learner has applied to multiple companies and receives an offer from the first
            company, the interview process with other companies will be automatically discontinued.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 7: OFFERS AND POST-OFFER PROCESS</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>7.1 Types of Offers</strong>
        </h3>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr className="bg-black-200">
                <th className={cellClass}>Offer Type</th>
                <th className={cellClass}>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={cellClass}>Job Offer</td>
                <td className={cellClass}>Final selection through a placement drive.</td>
              </tr>
              <tr>
                <td className={cellClass}>Pre-Placement Offer (PPO)</td>
                <td className={cellClass}>
                  Job offer made during/after an internship, training or project.
                </td>
              </tr>
              <tr>
                <td className={cellClass}>Internship Offer</td>
                <td className={cellClass}>
                  Training/internship offer without guaranteed job placement.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3 className={subheadingClass}>
          <strong>7.2 One Learner - One Offer Policy</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Once a learner secures a job offer, they are not eligible for further recruitment
            drives. Exceptions may apply on a case-by-case basis.
          </li>
          <li>
            Maximum participation limit: five interviews OR one job offer, after which the learner
            is removed from the CSP.
          </li>
          <li>Job location preferences are not entertained.</li>
        </ul>
        <h3 className={subheadingClass}>
          <strong>7.3 Post-Offer Processes</strong>
        </h3>
        <ul className={listClass}>
          <li>Learners must share their offer letters with PW Skills upon acceptance.</li>
          <li>Learners must follow due process for joining and reporting to companies.</li>
          <li>
            Learners consent that their career outcome details, along with their picture, may be
            posted on PW social media pages.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 8: MISCONDUCT, DEBARMENT AND WITHDRAWAL</strong>
        </h2>
        <h3 className={subheadingClass}>
          <strong>8.1 Misconduct &amp; Debarment</strong>
        </h3>
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr className="bg-black-200">
                <th className={cellClass}>Violation</th>
                <th className={cellClass}>Penalty</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={cellClass}>
                  Failure to attend the mandatory number of mock interviews during the course,
                  resulting in ineligibility to appear for the Employability Test (ET).
                </td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>Not applying for 3 consecutive opportunities</td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>Not attending 2 consecutive interviews after applying</td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>Cheating, misbehavior, or malpractice</td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>Providing false information in registration</td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>
                  Leaving the recruitment process midway or declining for any process once begun
                </td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>Contacting company officials without permission</td>
                <td className={cellClass}>Disqualification for 2 weeks</td>
              </tr>
              <tr>
                <td className={cellClass}>
                  Criticizing PW Skills publicly (social media, recruiters, etc.)
                </td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
              <tr>
                <td className={cellClass}>
                  Skipping preparatory sessions, workshops, or assessments
                </td>
                <td className={cellClass}>Permanent debarment</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3 className={subheadingClass}>
          <strong>8.2 Disciplinary Actions</strong>
        </h3>
        <ul className={listClass}>
          <li>Immediate removal from Career Services may apply for misconduct.</li>
          <li>Zero tolerance applies to dishonesty, misrepresentation and unethical practices.</li>
          <li>
            Learners will receive a written notice for misconduct, and PW Skills' decision is final.
          </li>
        </ul>
        <h3 className={subheadingClass}>
          <strong>8.3 Withdrawal</strong>
        </h3>
        <ul className={listClass}>
          <li>
            Learners are considered withdrawn if they fail to provide required details, do not
            register for shared job opportunities, or formally withdraw by notifying PW Skills.
          </li>
        </ul>
        <h3 className={subheadingClass}>
          <strong>8.4 Consequences of Withdrawal / Disqualification</strong>
        </h3>
        <ul className={listClass}>
          <li>
            No fee refunds or reimbursements are provided for withdrawn or disqualified learners.
          </li>
          <li>Learners may lose any job offers extended to them.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className={sectionHeadingClass}>
          <strong>CHAPTER 9: GENERAL PROVISIONS</strong>
        </h2>
        <ul className={listClass}>
          <li>
            <strong>No Placement Guarantee:</strong> CRP and Career Services are readiness and
            opportunity-support frameworks only. A readiness score does not guarantee internships,
            placements, interviews or employment.
          </li>
          <li>
            <strong>Right to Modify:</strong> PW Skills reserves the right, at its sole discretion,
            to modify assessment structures, scoring methodologies, eligibility criteria, readiness
            levels, interview allocation, or introduce additional assessment stages.
          </li>
          <li>
            <strong>Final Authority:</strong> All decisions on assessments, scoring, certification,
            readiness levels, eligibility, Career Services access and disciplinary matters rest
            solely with PW Skills and are final and binding.
          </li>
        </ul>
      </section>
    </div>
  );
};

export default CareerPolicy;
